/** Controlled item-row picker shared by Reprint and Refund drawers. */
import { Button, Input, tokens } from "@blurb/codex-react";
import { createStyles } from "@mantine/core";
import { GraphQL, GraphQLQueryData } from "@rpi/openapi-api";
import { IconV3 } from "@rpi/openapi-core";
import React, { forwardRef } from "react";
import { Body, Label } from "../../components/Typography";
import { RpaDropdown } from "../../components/inputFields/RpaDropdownInput";

type Order = GraphQLQueryData<GraphQL.OrderRefundReprintInfoQuery>;
export type OrderItem = NonNullable<Order["orderItems"]>[number];

export interface ItemSelectionRow {
  /** Stable React key for add/remove reconciliation. */
  id: string;
  /** Full reference; callers extract the wire field they need at submit. */
  selectedItem: OrderItem | undefined;
  /** Raw user input — kept as a string so partial input is representable. */
  value: string;
}

export type ValueColumnConfig =
  | { kind: "quantity"; label: string }
  | { kind: "amount"; label: string }
  | { kind: "percentage"; label: string };

interface OrderItemSelectorProps {
  orderItems: readonly OrderItem[];
  rows: ItemSelectionRow[];
  onRowsChange: (rows: ItemSelectionRow[]) => void;
  valueColumn: ValueColumnConfig;
  error?: string;
  /** Rendered when `orderItems` is empty. Defaults to nothing. */
  emptyState?: React.ReactNode;
}

const VALUE_INPUT_BY_KIND: Record<ValueColumnConfig["kind"], { step: number; max?: number; prefix?: string }> = {
  quantity: { step: 1 },
  amount: { step: 0.01, prefix: "$" },
  percentage: { step: 1, max: 100 },
};

const useStyles = createStyles({
  itemsWrapper: {
    flex: "auto",
    display: "grid",
    gridTemplateColumns: "3fr minmax(0, 1fr) auto",
    gap: tokens.spacing[2],
    alignItems: "center",
    paddingBottom: tokens.spacing[2],
  },
  itemsDivider: {
    gridColumn: "span 3",
    borderBottom: `1px solid ${tokens.color.semantic.border.faint}`,
  },
  itemsError: {
    gridColumn: "span 3",
  },
  itemSelecter: {
    gridColumn: "1",
    minWidth: 0,
  },
  valueInput: {
    gridColumn: "2",
    minWidth: 0,
    overflow: "hidden",
    "& > *": { minWidth: 0 },
    "& input[type=number]": {
      minWidth: 0,
      flex: "1 1 0",
      MozAppearance: "textfield",
    },
    "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },
  },
  removeButton: {
    gridColumn: "3",
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  itemLabel: {
    whiteSpace: "normal",
    wordBreak: "break-word" as const,
  },
});

export function OrderItemSelector({
  orderItems,
  rows,
  onRowsChange,
  valueColumn,
  error,
  emptyState,
}: OrderItemSelectorProps) {
  const { classes } = useStyles();

  if (orderItems.length === 0) {
    return <>{emptyState}</>;
  }

  const addRow = () => {
    onRowsChange([...rows, { id: crypto.randomUUID(), selectedItem: undefined, value: "" }]);
  };

  const removeRow = (index: number) => {
    onRowsChange(rows.filter((_, i) => i !== index));
  };

  const setRowItem = (index: number, item: OrderItem | undefined) => {
    onRowsChange(rows.map((row, i) => (i === index ? { ...row, selectedItem: item } : row)));
  };

  const setRowValue = (index: number, value: string) => {
    onRowsChange(rows.map((row, i) => (i === index ? { ...row, value } : row)));
  };

  const claimedKeys = new Set(rows.flatMap((row) => (row.selectedItem ? [getOptionKey(row.selectedItem)] : [])));

  const optionsForRow = (currentItem: OrderItem | undefined) => {
    const currentKey = currentItem ? getOptionKey(currentItem) : undefined;
    return orderItems
      .filter((item) => {
        const key = getOptionKey(item);
        return key === currentKey || !claimedKeys.has(key);
      })
      .map((item) => ({
        value: getOptionKey(item),
        label: `${getOptionKey(item)} - ${item.openapiProduct?.name ?? item.customerProduct?.name ?? "Item"}`,
        description: `(Qty. ${item.quantity ?? 0})`,
      }));
  };

  const canAddAnother = orderItems.length > rows.length && !rows.some((row) => row.selectedItem === undefined);

  return (
    <div>
      <div className={classes.itemsWrapper}>
        <Label size="base" weight="semibold">
          Item(s)
        </Label>
        <Label size="base" weight="semibold">
          {valueColumn.label}
        </Label>
        <div className={classes.itemsDivider} />

        {error && <Body className={classes.itemsError}>{error}</Body>}

        {rows.flatMap((row, index) => [
          <RpaDropdown
            key={`itemselect-${row.id}`}
            data-testid="order-item-select"
            className={classes.itemSelecter}
            placeholder="Select.."
            data={optionsForRow(row.selectedItem)}
            value={row.selectedItem ? getOptionKey(row.selectedItem) : null}
            onChange={(value) => setRowItem(index, value ? findItemByKey(orderItems, value) : undefined)}
            itemComponent={OrderItemSelectItem}
          />,
          <ValueInput
            key={`itemvalue-${row.id}`}
            valueColumn={valueColumn}
            value={row.value}
            disabled={!row.selectedItem}
            onChange={(value) => setRowValue(index, value)}
            className={classes.valueInput}
          />,
          <button
            key={`itemremove-${row.id}`}
            type="button"
            className={classes.removeButton}
            onClick={() => removeRow(index)}
            aria-label="Remove item"
          >
            <IconV3 icon="remove" size={24} />
          </button>,
        ])}
      </div>

      <Button
        data-testid="order-item-add"
        variant="outlined"
        intent="neutral"
        size="small"
        fullWidth
        onClick={addRow}
        disabled={!canAddAnother}
      >
        <IconV3 icon="add" size={16} />
        Add Item
      </Button>
    </div>
  );
}

interface ValueInputProps {
  valueColumn: ValueColumnConfig;
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
  className?: string;
}

function ValueInput({ valueColumn, value, disabled, onChange, className }: ValueInputProps) {
  const { step, max, prefix } = VALUE_INPUT_BY_KIND[valueColumn.kind];
  return (
    <Input
      type="number"
      min={0}
      step={step}
      max={max}
      value={value}
      placeholder="0"
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
      aria-label={valueColumn.label}
      className={className}
      iconLeft={prefix ? <Body>{prefix}</Body> : undefined}
    />
  );
}

interface OrderItemSelectItemProps extends React.ComponentPropsWithoutRef<"div"> {
  label: string;
  value: string;
  description?: string;
  "data-selected"?: boolean;
}

const OrderItemSelectItem = forwardRef<HTMLDivElement, OrderItemSelectItemProps>(
  ({ label, description, "data-selected": dataSelected, ...others }, ref) => {
    const { classes } = useStyles();
    return (
      <div ref={ref} {...others}>
        <Body weight={dataSelected ? "bold" : "normal"} className={classes.itemLabel}>
          {label}
        </Body>
        {description && <Body size="sm">{description}</Body>}
      </div>
    );
  }
);

OrderItemSelectItem.displayName = "OrderItemSelectItem";

/** Stable string key per item — used as the dropdown's `value`. */
function getOptionKey(item: OrderItem): string {
  return String(item.id);
}

function findItemByKey(items: readonly OrderItem[], key: string): OrderItem | undefined {
  return items.find((item) => getOptionKey(item) === key);
}
